import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const MONO    = "'JetBrains Mono', monospace";
const DISPLAY = "var(--font-display)";

export default function JoinTeamPage() {
  const { inviteToken } = useParams();
  const navigate        = useNavigate();

  // Read auth state from localStorage directly — this page works across all route sections
  const token    = localStorage.getItem('token');
  const userInfo = (() => { try { return JSON.parse(localStorage.getItem('userInfo') || 'null'); } catch { return null; } })();
  const role     = userInfo?.role;
  const isDriver = role === 'driver';
  const hasTeam  = isDriver && (userInfo?.driverId || userInfo?.id); // driverId = on team

  const [preview, setPreview] = useState(null);   // { teamName, expiresAt, usesLeft }
  const [loading, setLoading] = useState(true);
  const [joining, setJoining] = useState(false);
  const [error,   setError]   = useState(null);
  const [success, setSuccess] = useState(null);   // teamName after join

  useEffect(() => {
    axios.get(`/api/join/${inviteToken}`)
      .then(r => setPreview(r.data))
      .catch(err => setError(err.response?.data?.error || 'Einladungslink ungültig oder abgelaufen.'))
      .finally(() => setLoading(false));
  }, [inviteToken]);

  async function handleJoin() {
    setJoining(true);
    setError(null);
    try {
      const r = await axios.post(
        `/api/join/${inviteToken}`,
        {},
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setSuccess(r.data.teamName);
      // Reload after 2s so App.js re-fetches the driver profile with the new team
      setTimeout(() => { window.location.href = '/'; }, 2000);
    } catch (err) {
      setError(err.response?.data?.error || 'Fehler beim Beitreten. Bitte versuche es erneut.');
      setJoining(false);
    }
  }

  const containerStyle = {
    minHeight: '100vh',
    background: '#0a0c10',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: 24,
  };

  const cardStyle = {
    width: '100%',
    maxWidth: 440,
    background: 'rgba(17,19,24,0.98)',
    border: '1px solid rgba(var(--brand-primary-rgb),0.25)',
    padding: '36px 32px',
    boxShadow: '0 24px 64px rgba(0,0,0,0.7)',
  };

  const labelStyle = {
    fontFamily: MONO, fontSize: 9, fontWeight: 700,
    letterSpacing: '0.18em', color: 'var(--brand-primary)', marginBottom: 8,
  };

  const headingStyle = {
    fontFamily: DISPLAY, fontStyle: 'italic', fontWeight: 900,
    fontSize: 28, color: '#e2e2e8', textTransform: 'uppercase',
    lineHeight: 1.1, marginBottom: 8,
  };

  const subStyle = {
    fontFamily: MONO, fontSize: 10, color: '#849495',
    letterSpacing: '0.04em', lineHeight: 1.6, marginBottom: 24,
  };

  const btnPrimary = {
    width: '100%', padding: '14px 0',
    fontFamily: MONO, fontSize: 10, fontWeight: 700, letterSpacing: '0.12em',
    background: 'var(--brand-primary)', color: '#002022',
    border: 'none', cursor: 'pointer', textTransform: 'uppercase',
    opacity: joining ? 0.6 : 1,
  };

  const btnSecondary = {
    width: '100%', padding: '12px 0', marginTop: 10,
    fontFamily: MONO, fontSize: 9, fontWeight: 700, letterSpacing: '0.12em',
    background: 'transparent', color: '#849495',
    border: '1px solid rgba(59,73,75,0.5)', cursor: 'pointer', textTransform: 'uppercase',
  };

  if (loading) {
    return (
      <div style={containerStyle}>
        <div style={{ fontFamily: MONO, fontSize: 10, color: '#849495', letterSpacing: '0.12em' }}>
          LADE EINLADUNG…
        </div>
      </div>
    );
  }

  // ── Success ────────────────────────────────────────────────────────────────
  if (success) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ ...labelStyle, color: 'var(--brand-secondary)' }}>BEIGETRETEN</div>
          <div style={headingStyle}>{success}</div>
          <div style={subStyle}>Du bist jetzt Mitglied. Du wirst weitergeleitet…</div>
        </div>
      </div>
    );
  }

  // ── Error loading the link ─────────────────────────────────────────────────
  if (!preview) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ ...labelStyle, color: '#ff6b6b' }}>UNGÜLTIGER LINK</div>
          <div style={{ ...headingStyle, fontSize: 20 }}>Einladung nicht gefunden</div>
          <div style={subStyle}>{error}</div>
          <button style={{ ...btnSecondary, width: 'auto', padding: '10px 20px' }} onClick={() => navigate('/')}>
            ← Zurück zur App
          </button>
        </div>
      </div>
    );
  }

  const expiryStr = new Date(preview.expiresAt).toLocaleDateString('de-DE', {
    day: '2-digit', month: 'short', year: 'numeric',
  });

  // ── Not logged in ──────────────────────────────────────────────────────────
  if (!token || !userInfo) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={labelStyle}>TEAMEINLADUNG</div>
          <div style={headingStyle}>{preview.teamName}</div>
          <div style={subStyle}>
            Du wurdest eingeladen, diesem Team beizutreten.<br />
            Gültig bis {expiryStr}
            {preview.usesLeft !== null ? ` · noch ${preview.usesLeft} Plätze` : ''}.
          </div>
          <div style={{ padding: '16px', background: 'rgba(var(--brand-primary-rgb),0.05)', border: '1px solid rgba(var(--brand-primary-rgb),0.15)', marginBottom: 20 }}>
            <div style={{ fontFamily: MONO, fontSize: 10, color: '#849495', marginBottom: 12 }}>
              Melde dich an oder erstelle ein Konto, um dem Team beizutreten.
            </div>
            <a href={`/?next=/join/${inviteToken}`} style={{ ...btnPrimary, display: 'block', textAlign: 'center', textDecoration: 'none', padding: '12px 0' }}>
              ANMELDEN / REGISTRIEREN
            </a>
          </div>
        </div>
      </div>
    );
  }

  // ── Logged in as admin ─────────────────────────────────────────────────────
  if (role === 'admin') {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ ...labelStyle, color: '#ff9a1f' }}>HINWEIS</div>
          <div style={{ ...headingStyle, fontSize: 20 }}>Einladungslink</div>
          <div style={subStyle}>
            Team-Admins können über Einladungslinks kein anderes Team beitreten.<br />
            Dieser Link ist für Fahrer gedacht.
          </div>
          <button style={{ ...btnSecondary, width: 'auto', padding: '10px 20px' }} onClick={() => navigate('/')}>
            ← Zurück zum Dashboard
          </button>
        </div>
      </div>
    );
  }

  // ── Driver already on a team ───────────────────────────────────────────────
  if (hasTeam) {
    return (
      <div style={containerStyle}>
        <div style={cardStyle}>
          <div style={{ ...labelStyle, color: '#ff9a1f' }}>BEREITS IM TEAM</div>
          <div style={{ ...headingStyle, fontSize: 20 }}>Du bist bereits in einem Team</div>
          <div style={subStyle}>
            Du kannst nicht einem weiteren Team beitreten, solange du noch Mitglied eines anderen Teams bist.
          </div>
          <button style={{ ...btnSecondary, width: 'auto', padding: '10px 20px' }} onClick={() => navigate('/')}>
            ← Zurück
          </button>
        </div>
      </div>
    );
  }

  // ── Driver without team — can join ────────────────────────────────────────
  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={labelStyle}>TEAMEINLADUNG</div>
        <div style={headingStyle}>{preview.teamName}</div>
        <div style={subStyle}>
          Du wurdest eingeladen, diesem Team beizutreten.<br />
          Gültig bis {expiryStr}
          {preview.usesLeft !== null ? ` · noch ${preview.usesLeft} Plätze` : ''}.
        </div>

        {error && (
          <div style={{ fontFamily: MONO, fontSize: 10, color: '#ff6b6b', marginBottom: 16, padding: '10px 14px', background: 'rgba(255,107,107,0.08)', border: '1px solid rgba(255,107,107,0.2)' }}>
            {error}
          </div>
        )}

        {/* Team info card */}
        <div style={{ padding: '14px 16px', background: 'rgba(var(--brand-primary-rgb),0.04)', border: '1px solid rgba(var(--brand-primary-rgb),0.15)', marginBottom: 24 }}>
          <div style={{ fontFamily: MONO, fontSize: 9, color: '#849495', letterSpacing: '0.1em' }}>
            BEITRETEN ALS
          </div>
          <div style={{ fontFamily: MONO, fontSize: 13, color: '#e2e2e8', marginTop: 4, fontWeight: 700 }}>
            {userInfo?.name || 'Unbekannt'}
          </div>
        </div>

        <button style={btnPrimary} onClick={handleJoin} disabled={joining}>
          {joining ? '…BEITRETEN' : `TEAM BEITRETEN →`}
        </button>
        <button style={btnSecondary} onClick={() => navigate('/')}>
          ABBRECHEN
        </button>
      </div>
    </div>
  );
}
